import {
  CardConditionEnum,
  CardLanguageEnum,
  FinishEnum,
} from "modules/cards/model/card";
import type { FacetFilterValue } from "modules/common/cardFacets";
import { ShortTradeItem, ShortTradeList } from "modules/decks/model/deck";
import {
  type PricingConfiguration,
  ShortSet,
} from "modules/common/model/models";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import type { GameId } from "modules/common/model/models";
import type { ActiveTradeReference } from "modules/tradeLists/model/tradeList";

export interface Collection {
  createdAt: string;
  updatedAt: string;
  userId: string;
  id: string;
  pricing: PricingConfiguration;
}

export interface CollectionCard {
  game: GameId;
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  dataId: string;
  setCode: string;
  name: string;
  ids: string[];
  /** Exact mutable Collection-card IDs derived from linked Haves copies. */
  availableIds?: string[];
  quantity: number;
  /** Live, uncached reservation overlay for linked Haves copies. */
  spokenForQuantity?: number;
  activeTrades?: ActiveTradeReference[];
  updatedAt: string;
  createdAt: string;
  tradeItem?: ShortTradeItem;
  price?: EmbeddedCardPrice;
  purchasePrice?: number;
  types: string[];
  rarity: string;
  costPrinted: string;
  costValue: number;
  number: string;
}

export interface AddCollectionCardRequest {
  collectionId: string;
  cardId: string;
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  quantity: number;
  purchasePrice: number;
}

export interface RemoveCollectionCardRequest {
  id: string;
  collectionId: string;
}

export interface UpdateCollectionCardRequest {
  collectionId: string;
  ids: string[];
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  purchasePrice: number;
  quantity: number;
}

export interface UpdateCollectionCardResponse {
  collection: Collection;
  updatedLists: ShortTradeList[];
}

export enum CollectionCardSortOptions {
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  Name = "name",
  Price = "price",
  Quantity = "quantity",
}

export enum SortOrderEnum {
  Ascending = "asc",
  Descending = "desc",
  None = "",
}

export type FacetMatchMode = "and" | "or" | "exact";

export interface CollectionFacetFilters {
  // Field names and shapes come from the backend facet contract.
  [key: string]: FacetFilterValue;
}

export interface PagedCollectionCardsRequest extends CollectionFacetFilters {
  page: number;
  perPage: number;
  sort: CollectionCardSortOptions;
  order: SortOrderEnum;
  query: string;
  type: "all" | "traded" | "not-traded";
}

export interface FacetBucket {
  value: string | number;
  count: number;
  game?: string;
  label?: string;
}

export interface FacetCountsResponse {
  facets: Record<string, FacetBucket[]>;
  appraisal?: AppraiseCollectionResponse;
  pricing: PricingConfiguration;
  /** Grouped rows matching the complete facet request, when supported. */
  resultTotal?: number;
}

export interface CollectionFacetCountsResponse extends FacetCountsResponse {
  /** Collection facets always own the authoritative grouped-grid count. */
  resultTotal: number;
}

export interface PagedCollectionCardsResponse {
  cards: CollectionCard[];
  hasMore: boolean;
}

export interface AppraiseCollectionResponse {
  total: number;
  totalLength: number;
  distinctLength: number;
  pricing: PricingConfiguration;
}

// Re-export unified views for backward compatibility
export { UNIFIED_VIEWS as CollectionViews } from "modules/common/model/viewTypes";

export enum CardCollectionStatus {
  None,
  Some,
  All,
}

export interface AddedCollectionCard
  extends Omit<CollectionCard, "game" | "ids" | "quantity"> {
  id: string;
  game?: GameId;
  ids?: string[];
  quantity?: number;
}

export interface AddCollectionCardResponse {
  collection: Collection;
  updatedLists?: ShortTradeList[];
  card: AddedCollectionCard;
}

export interface AddMultipleCollectionCardsModel {
  itemId: string;
  cardId: string;
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
}

export interface AddMultipleCollectionCardsRequest {
  collectionId: string;
  cards: AddMultipleCollectionCardsModel[];
}

export interface AddMultipleCollectionCardsResponse {
  collection: Collection;
  updatedLists?: ShortTradeList[];
  added: number;
}

export interface CollectionCardSetsRequest {
  collectionId: string;
  page: number;
  perPage: number;
  query: string;
}

export interface CollectionCardSetsResponse {
  sets: ShortSet[];
  hasMore: boolean;
}

export const IMPORT_FORMAT_MTGGURU = "mtgguru";

export interface CollectionImportResponse {
  importId: string;
}

export enum CSVImportStatus {
  _ = 0,
  Processing = 1,
  Success = 2,
  Failed = 3,
  HasErrors = 4,
  Rollback = 5,
  Draft = 6,
}

export interface CollectionCSVImportPagedItem {
  createdAt: string;
  errs: string[] | null;
  id: string;
  imported: number;
  status: CSVImportStatus;
  type: string;
  updatedAt: string;
}

export interface CollectionCSVImportPagedRequest {
  page: number;
  perPage: number;
  collectionId: string;
}

export interface CollectionCSVImportPagedResponse {
  items: CollectionCSVImportPagedItem[];
  hasMore: boolean;
  total: number;
}

export interface CollectionCSVImport {
  collectionId: string;
  createdAt: string;
  errs: string[] | null;
  id: string;
  imported: number;
  status: CSVImportStatus;
  type: string;
  updatedAt?: string;
  userId: string;
  importedCards?: CollectionCard[];
  failedCards?: string[];
  totalRows?: number;
  validRows?: number;
  invalidRows?: number;
  totalCards?: number;
  validCards?: number;
  invalidCards?: number;
}

export interface CSVImportResponse {
  id: string;
}

export interface AddCardsToListRequest {
  listId: string;
  cards?: string[];
  filters?: PagedCollectionCardsRequest;
  quantityMode?: "min" | "max";
  quantityOverrides?: Record<string, number>;
}

export interface AddCardsToListResponse {
  added: number;
  message: string;
}

export interface RemoveCardsFromListResponse {
  removed: number;
  message: string;
  affectedListIds?: string[];
}

export interface DeleteBulkCollectionCardsRequest {
  collectionId: string;
  ids?: string[];
  remainingIds?: string[];
  filters?: PagedCollectionCardsRequest;
}

export interface DeleteBulkCollectionCardsResponse {
  count: number;
  affectedListIds?: string[];
}

export interface SelectAllCountResponse {
  total: number;
  groups: number;
  listed: number;
  unlisted: number;
  reserved: number;
}
